import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, endOfDay, format } from 'date-fns';

export interface ConnectionDetail {
  id: string;
  type: 'call_transfer' | 'video_call';
  date: string;
  contactName: string;
  contactId: string | null;
  dealId: string | null;
  dealName: string | null;
}

interface DateRange {
  from: Date | null;
  to: Date | null;
}

export function useSDRConnectionDetails(
  sdrUserId: string | null,
  dateRange?: DateRange
) {
  const [connections, setConnections] = useState<ConnectionDetail[]>([]);
  const [loading, setLoading] = useState(false);

  const loadDetails = useCallback(async () => {
    if (!sdrUserId) {
      setConnections([]);
      return;
    }
    
    setLoading(true);

    try {
      // Build date filters
      const isAllTime = dateRange && dateRange.from === null && dateRange.to === null;
      const startDate = isAllTime 
        ? '2020-01-01' 
        : dateRange?.from 
          ? format(startOfDay(dateRange.from), 'yyyy-MM-dd') 
          : null;
      const endDate = dateRange?.to 
        ? format(endOfDay(dateRange.to), 'yyyy-MM-dd\'T\'23:59:59') 
        : null;

      // Get SDR email for attendees fallback
      const { data: sdrUser } = await supabase
        .from('app_users')
        .select('email')
        .eq('id', sdrUserId)
        .single();

      // Query 1: Call transfers (where this SDR transferred the call)
      // Type assertion to avoid TS2589 "Type instantiation is excessively deep" with complex joins
      const transferSelectQuery = `
          id, 
          transferred_at, 
          contact_id, 
          deal_id,
          contact:contacts!contact_calls_contact_id_fkey(first_name, last_name),
          deal:deals(id, name)
        `;
      let transfersQuery: any = (supabase.from('calls') as any)
        .select(transferSelectQuery)
        .eq('transferred_by', sdrUserId);
        // NOTE: Removed .not('transferred_to', 'is', null) to count "Ring All" transfers

      if (startDate) {
        transfersQuery = transfersQuery.gte('transferred_at', startDate);
      }
      if (endDate) {
        transfersQuery = transfersQuery.lte('transferred_at', endDate);
      }

      // Query 2: Confirmed video calls (where this SDR scheduled the meeting)
      const videoSelectQuery = `
          id, 
          completed_at,
          scheduled_for,
          contact_id, 
          deal_id,
          scheduled_by,
          contact:contacts!contact_calls_contact_id_fkey(first_name, last_name),
          deal:deals(id, name)
        `;
      let videoQuery: any = (supabase.from('calls') as any)
        .select(videoSelectQuery)
        .eq('channel', 'video')
        .eq('status', 'completed')
        .eq('connection_confirmed', true)
        .eq('scheduled_by', sdrUserId);

      if (startDate) {
        videoQuery = videoQuery.gte('completed_at', startDate);
      }
      if (endDate) {
        videoQuery = videoQuery.lte('completed_at', endDate);
      }

      // Query 3: Video Now (ring requests) - confirmed connections
      let ringQuery = supabase
        .from('video_call_ring_requests' as any)
        .select(`
          id,
          accepted_at,
          contact_id,
          deal_id,
          contact:contacts(first_name, last_name),
          deal:deals(id, name)
        `)
        .eq('requested_by', sdrUserId)
        .eq('status', 'accepted')
        .eq('connection_confirmed', true);

      if (startDate) {
        ringQuery = ringQuery.gte('accepted_at', startDate);
      }
      if (endDate) {
        ringQuery = ringQuery.lte('accepted_at', endDate);
      }

      // Execute queries in parallel
      const [transfersResult, videoResult, ringResult] = await Promise.all([
        transfersQuery,
        videoQuery,
        ringQuery
      ]);

      const { data: transfers, error: transfersError } = transfersResult;
      const { data: videoCalls, error: videoError } = videoResult;
      const { data: ringRequests, error: ringError } = ringResult;
      
      if (transfersError) {
        console.error('Error fetching transfers:', transfersError);
      }
      if (videoError) {
        console.error('Error fetching video calls:', videoError);
      }
      if (ringError) {
        console.error('Error fetching ring requests:', ringError);
      }

      // Query 4: Video calls where SDR is in attendees (fallback for legacy data)
      let attendeeConnections: ConnectionDetail[] = [];
      if (sdrUser?.email) {
        const attendeeSelectQuery = `
            id,
            completed_at,
            scheduled_for,
            contact_id,
            deal_id,
            scheduled_by,
            attendees,
            contact:contacts!contact_calls_contact_id_fkey(first_name, last_name),
            deal:deals(id, name)
          `;
        let attendeesQuery: any = (supabase.from('calls') as any)
          .select(attendeeSelectQuery)
          .eq('channel', 'video')
          .eq('status', 'completed')
          .eq('connection_confirmed', true)
          .is('scheduled_by', null)
          .contains('attendees', [sdrUser.email]);

        if (startDate) {
          attendeesQuery = attendeesQuery.gte('completed_at', startDate);
        }
        if (endDate) {
          attendeesQuery = attendeesQuery.lte('completed_at', endDate);
        }

        const { data: attendeeCalls, error: attendeeError } = await attendeesQuery;
        
        if (attendeeError) {
          console.error('Error fetching attendee calls:', attendeeError);
        }

        // Process attendee calls
        attendeeConnections = (attendeeCalls || []).map((v: any) => ({
          id: v.id,
          type: 'video_call' as const,
          date: v.completed_at || v.scheduled_for,
          contactName: v.contact 
            ? `${v.contact.first_name || ''} ${v.contact.last_name || ''}`.trim() || 'Sem nome'
            : 'Contato não identificado',
          contactId: v.contact_id,
          dealId: v.deal_id,
          dealName: v.deal?.name || null,
        }));
      }

      // Query 5: Video calls created by SDR (fallback when scheduled_by is NULL — e.g. Video Now natives)
      const createdBySelectQuery = `
          id,
          completed_at,
          scheduled_for,
          contact_id,
          deal_id,
          created_by,
          contact:contacts!contact_calls_contact_id_fkey(first_name, last_name),
          deal:deals(id, name)
        `;
      let createdByQuery: any = (supabase.from('calls') as any)
        .select(createdBySelectQuery)
        .eq('channel', 'video')
        .eq('status', 'completed')
        .eq('connection_confirmed', true)
        .is('scheduled_by', null)
        .eq('created_by', sdrUserId);

      if (startDate) {
        createdByQuery = createdByQuery.gte('completed_at', startDate);
      }
      if (endDate) {
        createdByQuery = createdByQuery.lte('completed_at', endDate);
      }

      const { data: createdByCalls, error: createdByError } = await createdByQuery;

      if (createdByError) {
        console.error('Error fetching created_by calls:', createdByError);
      }

      const createdByConnections: ConnectionDetail[] = (createdByCalls || []).map((v: any) => ({
        id: v.id,
        type: 'video_call' as const,
        date: v.completed_at || v.scheduled_for,
        contactName: v.contact
          ? `${v.contact.first_name || ''} ${v.contact.last_name || ''}`.trim() || 'Sem nome'
          : 'Contato não identificado',
        contactId: v.contact_id,
        dealId: v.deal_id,
        dealName: v.deal?.name || null,
      }));

      // Process transfers
      const transferConnections: ConnectionDetail[] = (transfers || []).map((t: any) => ({
        id: t.id,
        type: 'call_transfer' as const,
        date: t.transferred_at,
        contactName: t.contact 
          ? `${t.contact.first_name || ''} ${t.contact.last_name || ''}`.trim() || 'Sem nome'
          : 'Contato não identificado',
        contactId: t.contact_id,
        dealId: t.deal_id,
        dealName: t.deal?.name || null,
      }));

      // Process scheduled video calls
      const videoConnections: ConnectionDetail[] = (videoCalls || []).map((v: any) => ({
        id: v.id,
        type: 'video_call' as const,
        date: v.completed_at || v.scheduled_for,
        contactName: v.contact 
          ? `${v.contact.first_name || ''} ${v.contact.last_name || ''}`.trim() || 'Sem nome'
          : 'Contato não identificado',
        contactId: v.contact_id,
        dealId: v.deal_id,
        dealName: v.deal?.name || null,
      }));

      // Process ring requests (Video Now)
      const ringConnections: ConnectionDetail[] = (ringRequests || []).map((r: any) => ({
        id: r.id,
        type: 'video_call' as const,
        date: r.accepted_at,
        contactName: r.contact 
          ? `${r.contact.first_name || ''} ${r.contact.last_name || ''}`.trim() || 'Sem nome'
          : 'Contato não identificado',
        contactId: r.contact_id,
        dealId: r.deal_id,
        dealName: r.deal?.name || null,
      }));

      // Combine all sources and remove duplicates (by id)
      const allConnections = [...transferConnections, ...videoConnections, ...ringConnections, ...attendeeConnections, ...createdByConnections]
        .filter(c => c.date)
        // Remove duplicates by id
        .filter((conn, index, self) => self.findIndex(c => c.id === conn.id) === index)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setConnections(allConnections);
    } catch (error) {
      console.error('Error loading SDR connection details:', error);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, [sdrUserId, dateRange?.from?.getTime(), dateRange?.to?.getTime()]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  return { connections, loading, refresh: loadDetails };
}
